import { type default as React, useCallback, useContext, useState } from 'react';

import type { ProductId } from '../../common/ui/app-tile/app-selector/types';
import type { AppTileConfig } from '../../types';

import type { KeyboardReorderContextValue } from './types';

import { KeyboardReorderContext } from './index';

/**
 * Hook to access keyboard reorder context.
 * Throws an error if used outside of KeyboardReorderProvider.
 */
export function useKeyboardReorderContext(): KeyboardReorderContextValue {
	const context = useContext(KeyboardReorderContext);

	if (!context) {
		throw new Error('useKeyboardReorderContext must be used within a KeyboardReorderProvider');
	}

	return context;
}

export function useAppTilesState(): {
	appTiles: AppTileConfig[];
	onReorder: ((apps: AppTileConfig[]) => void) | null;
	setAppTiles: React.Dispatch<React.SetStateAction<AppTileConfig[]>>;
	setOnReorder: React.Dispatch<React.SetStateAction<((apps: AppTileConfig[]) => void) | null>>;
	setTemporaryOrder: React.Dispatch<React.SetStateAction<AppTileConfig[]>>;
	temporaryOrder: AppTileConfig[];
} {
	const [appTiles, setAppTiles] = useState<AppTileConfig[]>([]);
	const [temporaryOrder, setTemporaryOrder] = useState<AppTileConfig[]>(appTiles);
	const [onReorder, setOnReorder] = useState<((apps: AppTileConfig[]) => void) | null>(null);

	return {
		appTiles,
		setAppTiles,
		temporaryOrder,
		setTemporaryOrder,
		onReorder,
		setOnReorder,
	};
}

export function useFocusSelectedTile(
	selectedProductId: ProductId | null,
	tileRefsMap: React.MutableRefObject<Map<ProductId, HTMLAnchorElement>>,
): () => void {
	return useCallback(() => {
		if (!selectedProductId) {
			return;
		}
		const element = tileRefsMap.current.get(selectedProductId);
		element?.focus();
	}, [tileRefsMap, selectedProductId]);
}

export function useMoveSelectedTile(
	temporaryOrder: AppTileConfig[],
	setTemporaryOrder: React.Dispatch<React.SetStateAction<AppTileConfig[]>>,
	selectedProductId: ProductId | null,
	focusSelectedTile: () => void,
): (direction: 'up' | 'down') => void {
	return useCallback(
		(direction: 'up' | 'down') => {
			const selectedIndex = temporaryOrder.findIndex((app) => app.productId === selectedProductId);
			const selectedTile = temporaryOrder[selectedIndex];

			if (!selectedTile) {
				return;
			}

			const newIndex = direction === 'down' ? selectedIndex + 1 : selectedIndex - 1;

			if (newIndex < 0 || newIndex >= temporaryOrder.length) {
				return;
			}

			const newOrder = [...temporaryOrder];
			newOrder.splice(selectedIndex, 1);
			newOrder.splice(newIndex, 0, selectedTile);
			setTemporaryOrder(newOrder);

			focusSelectedTile();
		},
		[temporaryOrder, selectedProductId, focusSelectedTile, setTemporaryOrder],
	);
}
